import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  FC,
  useLayoutEffect,
  RefObject,
} from 'react';
// mui components
import { Grid, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/styles';
// components
import PhotoViewer, { IGalleryProps } from 'components/photo-viewer';
import { PlayerWithCachedHeight } from 'components/uploaded-images/components/player-with-cached-height';
import MediaItem from './components/media-item';
import RecentActivityItem from './components/recent-activity-item';
import ActivityFeedMediaItems from './components/activity-feed-items';
// types
import { IPostUploads, IUpdatePostBody, IUploadingFiles } from 'redux-store/api/activity-feed';
import { IStyles, IUploadedFilesData } from 'redux-store/types';
// helpers
import { isAudio, isUploadsFromPost, isVideo } from './helpers';
// constants
import { DOCUMENT_FORMATS } from 'utils/constants';
// styles
import { uploadedImagesStyles } from './styles/styles';

export interface IUploadedImagesProps {
  type: 'recent_activity' | 'activity_feed' | 'new_images';
  uploads?: IUploadedFilesData[] | IPostUploads[];
  uploadingFiles?: IUploadingFiles[];
  visibleImagesCount?: number;
  imageStyles?: IStyles;
  containerStyles?: IStyles;
  handleDeleteUploadedData?: (fileId: string) => void;
  editablePostBody?: IUpdatePostBody;
  isDescriptionEdtable?: boolean;
  /** Non–FC: parent persists caption (e.g. optimistic merge + `onUpdatePost`). Omit when captions are persisted only via File Cabinet PATCH + refresh. */
  updateDescription?: (id: string, description: string) => void;
  videoHeightCacheRef?: RefObject<Record<string, number>>;
  isActivityFeedMedia?: boolean;
}

const UploadedImages: FC<IUploadedImagesProps> = ({
  uploads,
  uploadingFiles,
  containerStyles,
  visibleImagesCount,
  type,
  handleDeleteUploadedData,
  editablePostBody,
  isDescriptionEdtable,
  updateDescription,
  videoHeightCacheRef,
  isActivityFeedMedia,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [openImageViewer, setOpenImageViewer] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);

  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [containerHeight, setContainerHeight] = useState<number>(0);

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));

  useLayoutEffect(() => {
    if (containerRef.current?.offsetWidth) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
    if (containerRef.current?.offsetHeight) {
      setContainerHeight(containerRef.current.offsetHeight);
    }
  }, [uploads?.length]);

  const handleOpenModal = useCallback((index: number) => {
    setOpenImageViewer(true);
    setActivePhoto(index);
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpenImageViewer(false);
  }, []);

  const multimedia: IPostUploads[] | IUploadedFilesData[] = useMemo(() => {
    if (!uploads?.length) {
      return [];
    }

    if (isUploadsFromPost(uploads)) {
      if (type === 'activity_feed') {
        return uploads.filter(
          ({ name, mimeType }) =>
            !DOCUMENT_FORMATS.includes(name.substring(name.lastIndexOf('.'))) && !isAudio(mimeType)
        );
      }

      return uploads.filter(({ name }) => {
        return !DOCUMENT_FORMATS.includes(name.substring(name.lastIndexOf('.')));
      });
    }

    return uploads.filter(
      ({ name }) => !DOCUMENT_FORMATS.includes(name.substring(name.lastIndexOf('.')))
    );
  }, [uploads, type]);

  const uploadingImages = useMemo(
    () =>
      uploadingFiles
        ? uploadingFiles.filter(
            ({ name }) => !DOCUMENT_FORMATS.includes(name.substring(name.lastIndexOf('.')))
          )
        : [],
    [uploadingFiles]
  );

  const hiddenImagesCount = useMemo(
    () => multimedia.length - visibleImagesCount,
    [multimedia.length, visibleImagesCount]
  );

  const galleryPhotos: IGalleryProps['imagesList'] = useMemo(
    () =>
      uploadingImages.length
        ? uploadingImages
            .filter(({ mimeType }) => !isAudio(mimeType))
            .map(
              (
                { url, mimeType, previewUrl, description, location, timestamp, id, fileCabinetId },
                index
              ) => ({
                id: index,
                imageSrc: url,
                thumbnail: url,
                previewUrl: isVideo(mimeType) ? previewUrl : '',
                mimeType,
                itemId: id,
                description,
                location,
                timestamp,
                fileCabinetId,
              })
            )
        : isUploadsFromPost(multimedia)
          ? multimedia
              .filter(({ mimeType }) => !isAudio(mimeType))
              .map(({ mimeType, url, options, id, description, location, timestamp, profile, createdAt, fileCabinetId }, index) => ({
                id: index,
                imageSrc: url,
                thumbnail: url,
                mimeType,
                previewUrl: isVideo(mimeType) ? options?.previewUrl : '',
                itemId: id,
                description,
                location,
                timestamp,
                profile,
                createdAt,
                fileCabinetId,
              }))
          : multimedia.map((image, index) => ({
              id: index,
              imageSrc: image.url,
              thumbnail: image.url,
              itemId: image.id,
              ...('description' in image &&
                typeof image.description === 'string' && { description: image.description }),
            })),
    [uploadingImages, multimedia]
  );

  const StyledBigImage = styled('img')(() => ({
    width: '100%',
    height: '100%',
    ...(uploadedImagesStyles.bigImage as IStyles),
    objectFit: 'cover',
  }));

  const recentActivityMedia =
    type === 'recent_activity'
      ? multimedia.slice(0, visibleImagesCount + 1).map((item, index) => {
          return (
            <RecentActivityItem
              key={item.id}
              item={item}
              handleOpenModal={handleOpenModal}
              index={index}
              hiddenImagesCount={hiddenImagesCount}
              visibleImagesCount={visibleImagesCount}
            />
          );
        })
      : null;

  const activityFeedMedia =
    type === 'activity_feed' && containerWidth && containerHeight ? (
      <ActivityFeedMediaItems
        multimedia={multimedia}
        visibleImagesCount={visibleImagesCount}
        hiddenImagesCount={hiddenImagesCount}
        handleOpenModal={handleOpenModal}
        containerHeight={containerHeight}
        containerWidth={containerWidth}
      />
    ) : null;

  const entityMedia =
    type === 'new_images'
      ? uploadingImages.map((item) => {
          const index = galleryPhotos?.find(({ itemId }) => itemId === item.id)?.id;
          return (
            <MediaItem
              key={item.tempId}
              item={item}
              onDelete={handleDeleteUploadedData}
              index={index}
              handleOpenModal={handleOpenModal}
            />
          );
        })
      : null;

  if (!multimedia.length && !uploadingImages.length) {
    return null;
  }

  if (multimedia.length === 1 && type === 'activity_feed') {
    if (isUploadsFromPost(multimedia)) {
      const currentItem = multimedia[0];
      if (currentItem?.mimeType && isVideo(currentItem.mimeType)) {
        return (
          <PlayerWithCachedHeight
            containerWidth={containerWidth}
            url={currentItem.url}
            previewUrl={currentItem.options.previewUrl}
            uploadId={currentItem.id}
            videoHeightCacheRef={videoHeightCacheRef}
            containerRef={containerRef}
          />
        );
      }
    }

    const currentImage = multimedia[0];
    const imageHeight = isTablet ? '500px' : '300px';

    return (
      <Grid ref={containerRef} sx={{ ...uploadedImagesStyles.bigImageContainer, height: imageHeight }}>
        {containerWidth ? (
          <>
            <StyledBigImage
              src={currentImage.url}
              alt={currentImage.name}
              onClick={() => handleOpenModal(0)}
            />
            <PhotoViewer
              isOpen={openImageViewer}
              handleClose={handleCloseModal}
              activeIndex={activePhoto}
              imagesList={galleryPhotos}
              editablePostData={editablePostBody}
              isDescriptionEditable={isDescriptionEdtable}
              updateDescription={updateDescription}
              isActivityFeedMedia={isActivityFeedMedia}
            />
          </>
        ) : null}
      </Grid>
    );
  }

  return (
    <Grid ref={containerRef} sx={containerStyles ?? uploadedImagesStyles.container}>
      {type === 'recent_activity'
        ? recentActivityMedia
        : type === 'activity_feed' && containerWidth && containerHeight
          ? activityFeedMedia
          : type === 'new_images'
            ? entityMedia
            : null}
      <PhotoViewer
        isOpen={openImageViewer}
        handleClose={handleCloseModal}
        activeIndex={activePhoto}
        imagesList={galleryPhotos}
        editablePostData={editablePostBody}
        isDescriptionEditable={isDescriptionEdtable}
        updateDescription={updateDescription}
        isActivityFeedMedia={isActivityFeedMedia}
      />
    </Grid>
  );
};

export default UploadedImages;
